import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Application } from "../../types";

const COLORS = ["#005BBB", "#F1A208", "#E63946", "#7FB069", "#8338EC"];

interface ChartsDashboardProps {
  applications: Application[];
}

const ChartsDashboard: React.FC<ChartsDashboardProps> = ({ applications }) => {
  const perDate = useMemo(() => {
    const grouped: Record<string, number> = {};
    applications.forEach((app) => {
      const date = new Date(app.registrationDate).toISOString().slice(0, 10);
      grouped[date] = (grouped[date] || 0) + 1;
    });
    return Object.entries(grouped).map(([date, count]) => ({
      _id: date,
      count,
    }));
  }, [applications]);

  const byFaculty = useMemo(() => {
    const grouped: Record<string, number> = {};
    applications.forEach((app) => {
      grouped[app.studentFaculty] = (grouped[app.studentFaculty] || 0) + 1;
    });
    return Object.entries(grouped).map(([name, count]) => ({
      _id: name,
      count,
    }));
  }, [applications]);

  const byCategory = useMemo(() => {
    const grouped: Record<string, number> = {};
    applications.forEach((app) => {
      grouped[app.businessCategory] = (grouped[app.businessCategory] || 0) + 1;
    });
    return Object.entries(grouped).map(([name, count]) => ({
      _id: name,
      count,
    }));
  }, [applications]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
      <div className="bg-white rounded-xl p-4 shadow border border-gray-200">
        <h3 className="font-semibold text-[var(--uat-blue)] mb-2 text-center">
          Registros por Fecha
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={perDate}>
            <XAxis dataKey="_id" />
            <YAxis />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#005BBB"
              strokeWidth={3}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl p-4 shadow border border-gray-200">
        <h3 className="font-semibold text-[var(--uat-blue)] mb-2 text-center">
          Participación por Carrera
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={byFaculty}>
            <XAxis dataKey="_id" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="count" fill="#E63946" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl p-4 shadow border border-gray-200">
        <h3 className="font-semibold text-[var(--uat-blue)] mb-2 text-center">
          Categorías de Emprendimiento
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={byCategory}
              cx="50%"
              cy="50%"
              outerRadius={80}
              dataKey="count"
              nameKey="_id"
              label
            >
              {byCategory.map((_, idx) => (
                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Pie>
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ChartsDashboard;
